"use client";
import { Room } from "livekit-client";
import { useTranslations } from "next-intl";
import { Logger } from "@shared/utils/logger";
import { toastNotify } from "@client/components/ui/toast";
import type { MuteRequestPayload } from "@client/types/livekit.types";
import { useCallback, useState, useRef, useMemo, useEffect } from "react";

interface UseMuteNotificationOptions {
  room?: Room;
}

interface MuteState {
  audio: boolean;
  video: boolean;
}

interface UseMuteNotificationReturn {
  /** Whether the user was muted by the host */
  wasMutedByHost: boolean;
  /** Whether the user's video was disabled by the host */
  wasVideoMutedByHost: boolean;
  /** Handler for mute request data channel messages */
  handleMuteRequest: (payload: MuteRequestPayload) => void;
  /** Handler when host stops all screen shares */
  handleHostStopScreenShare: () => void;
  /** Clear the muted by host state */
  clearMutedByHost: () => void;
}

// Toast config constants
const TOAST_DURATION_WARNING = { duration: 5000 };

/**
 * Handles notifications when a participant is muted/unmuted by host.
 * Also manages the "Request to Change Media" flow via interactive Toasts.
 */
export const MuteNotification = (options?: UseMuteNotificationOptions): UseMuteNotificationReturn => {
  // Existing mute state (for tracking host actions)
  const [muteState, setMuteState] = useState<MuteState>({ audio: false, video: false });
  const t = useTranslations();

  // Stable ref for room - updated in effect to avoid render-time ref access
  const roomRef = useRef<Room | undefined>(options?.room);
  useEffect(() => {
    roomRef.current = options?.room;
  }, [options?.room]);

  const handleMuteRequest = useCallback(
    (payload: MuteRequestPayload) => {
      // Cast action to string to avoid type errors until types are updated
      const { action, trackType, mutedBy } = payload;
      const hostLabel = mutedBy === "host" ? "The host" : mutedBy;

      if (action === "mute") {
        const room = roomRef.current;
        const lp = room?.localParticipant;
        if (trackType === "audio") {
          void lp?.setMicrophoneEnabled(false).catch((error) => {
            Logger.error("MuteNotification", "Failed to mute microphone after host mute", error);
          });
          setMuteState((prev) => (prev.audio ? prev : { ...prev, audio: true }));
          toastNotify.warning(`${hostLabel} ${t("toast.warning.micMutedByHost")}`, TOAST_DURATION_WARNING);
        } else {
          void lp?.setCameraEnabled(false).catch((error) => {
            Logger.error("MuteNotification", "Failed to disable camera after host mute", error);
          });
          setMuteState((prev) => (prev.video ? prev : { ...prev, video: true }));
          toastNotify.warning(`${hostLabel} ${t("toast.warning.cameraDisabledByHost")}`, TOAST_DURATION_WARNING);
        }
        return;
      }

      // Toast Content & Styles
      const isMuteRequest = action === "request-mute";
      const _isUnmuteRequest = action === "request-unmute";
      const isAudio = trackType === "audio";

      // Theme Config (using Custom Professional Blue)
      // User requested #0072a2
      const theme = {
        border: "border-l-4 border-[#0072a2]",
        bg: "bg-white/95", // Kept clean white/glass
        iconBg: "bg-[#0072a2]/10",
        iconColor: "text-[#0072a2]",
        buttonBg: "bg-[#0072a2] text-white hover:bg-[#006089]", // Darker shade for hover
        titleColor: "text-gray-900"
      };

      const title = isMuteRequest
        ? isAudio
          ? t("toast.muteRequest.muteMic.title")
          : t("toast.muteRequest.turnOffCamera.title")
        : isAudio
          ? t("toast.muteRequest.unmuteMic.title")
          : t("toast.muteRequest.turnOnCamera.title");

      const message = isMuteRequest
        ? isAudio
          ? `${hostLabel} ${t("toast.muteRequest.muteMic.message")}`
          : `${hostLabel} ${t("toast.muteRequest.turnOffCamera.message")}`
        : isAudio
          ? `${hostLabel} ${t("toast.muteRequest.unmuteMic.message")}`
          : `${hostLabel} ${t("toast.muteRequest.turnOnCamera.message")}`;

      const acceptLabel = isMuteRequest
        ? isAudio
          ? t("toast.muteRequest.muteMic.button")
          : t("toast.muteRequest.turnOffCamera.button")
        : isAudio
          ? t("toast.muteRequest.unmuteMic.button")
          : t("toast.muteRequest.turnOnCamera.button");

      const declineLabel = t("toast.muteRequest.cancel");

      // Show Custom Toast (Replicating ToastItem structure)
      // Show Custom Toast (Replicating ToastItem structure)
      toastNotify.custom(
        (toastItem) => (
          <div
            className={`${
              toastItem.visible
                ? "animate-in slide-in-from-top-2 fade-in zoom-in-95 duration-300 ease-out"
                : "animate-out slide-out-to-top-2 fade-out zoom-out-95 duration-200"
            } pointer-events-auto relative flex w-full max-w-sm rounded-xl bg-white/95 p-3 backdrop-blur-md transition-all duration-300 shadow-lg ring-1 ring-black/5 ${theme.border}`}
          >
            <div className="flex flex-1 items-start gap-3">
              {/* Icon Wrapper */}
              <div className={`shrink-0 p-1.5 rounded-lg ${theme.iconBg}`}>
                {isAudio ? (
                  <svg className={`h-5 w-5 ${theme.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={
                        isMuteRequest
                          ? "M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                          : "M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                      }
                    />
                  </svg>
                ) : (
                  <svg className={`h-5 w-5 ${theme.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={
                        isMuteRequest
                          ? "M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                          : "M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      }
                    />
                  </svg>
                )}
              </div>

              {/* Text & Buttons Wrapper */}
              <div className="flex-1 space-y-1">
                <p className={`font-semibold text-sm ${theme.titleColor}`}>{title}</p>
                <p className="text-sm text-gray-600 leading-relaxed">{message}</p>

                <div className="mt-2 flex gap-2 justify-end">
                  <button
                    onClick={async () => {
                      toastNotify.dismiss(toastItem.id); // Dismiss FIRST
                      const room = roomRef.current;
                      if (!room?.localParticipant) return;

                      try {
                        if (isAudio) {
                          if (isMuteRequest) {
                            await room.localParticipant.setMicrophoneEnabled(false);
                            setMuteState((prev) => (prev.audio ? prev : { ...prev, audio: true }));
                          } else {
                            await room.localParticipant.setMicrophoneEnabled(true);
                            setMuteState((prev) => (!prev.audio ? prev : { ...prev, audio: false }));
                          }
                        } else {
                          if (isMuteRequest) {
                            await room.localParticipant.setCameraEnabled(false);
                            setMuteState((prev) => (prev.video ? prev : { ...prev, video: true }));
                          } else {
                            await room.localParticipant.setCameraEnabled(true);
                            setMuteState((prev) => (!prev.video ? prev : { ...prev, video: false }));
                          }
                        }
                      } catch (error) {
                        Logger.error("MuteNotification", "Failed to apply media change", error);
                        // Only show error if it fails
                        toastNotify.error(t("toast.media.mediaUpdateFailed"));
                      }
                    }}
                    className={`px-3 py-1.5 text-xs font-semibold rounded shadow-sm transition-colors ${theme.buttonBg}`}
                  >
                    {acceptLabel}
                  </button>
                  <button
                    onClick={() => toastNotify.dismiss(toastItem.id)}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 hover:text-gray-800 rounded shadow-sm transition-colors"
                  >
                    {declineLabel}
                  </button>
                </div>
              </div>

              {/* Close X */}
              <button
                onClick={() => toastNotify.dismiss(toastItem.id)}
                className="absolute top-3 right-3 shrink-0 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </div>
        ),
        { duration: Infinity }
      );
    },
    [t]
  );

  const handleHostStopScreenShare = useCallback(() => {
    const room = roomRef.current;
    const lp = room?.localParticipant;
    void lp
      ?.setScreenShareEnabled(false)
      .then(() => {
        toastNotify.warning(t("toast.warning.screenShareStoppedByHost"), TOAST_DURATION_WARNING);
      })
      .catch((error) => {
        Logger.error("MuteNotification", "Failed to stop screen share after host action", error);
      });
  }, [t]);

  const clearMutedByHost = useCallback(() => {
    setMuteState((prev) => (!prev.audio && !prev.video ? prev : { audio: false, video: false }));
  }, []);

  return useMemo(
    () => ({
      wasMutedByHost: muteState.audio,
      wasVideoMutedByHost: muteState.video,
      handleMuteRequest,
      handleHostStopScreenShare,
      clearMutedByHost
    }),
    [muteState.audio, muteState.video, handleMuteRequest, handleHostStopScreenShare, clearMutedByHost]
  );
};
